import { forwardRef, memo } from 'react';
import { Eye, EyeOff, GripVertical, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import type { SiteBlock } from '../../types/site';
import { BLOCK_TYPE_LABELS } from '../../types/site';

interface BlockItemProps {
  block: SiteBlock;
  isFirst: boolean;
  isLast: boolean;
  isProtected: boolean;
  isFocused?: boolean;
  onToggleVisibility: (blockId: string, visible: boolean) => void;
  onEdit: (block: SiteBlock) => void;
  onDelete: (blockId: string) => void;
  onMoveUp: (blockId: string) => void;
  onMoveDown: (blockId: string) => void;
}

function getBlockTitle(block: SiteBlock): string {
  const settings = block.settings as Record<string, unknown>;
  if (settings.title && typeof settings.title === 'string') {
    return settings.title;
  }
  return BLOCK_TYPE_LABELS[block.type] || block.type;
}

export const BlockItem = memo(forwardRef<HTMLDivElement, BlockItemProps>(function BlockItem({
  block,
  isFirst,
  isLast,
  isProtected,
  isFocused,
  onToggleVisibility,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}, ref) {
  return (
    <div
      ref={ref}
      className={`flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-800 border rounded-xl transition-all duration-300 ${
        isFocused
          ? 'border-primary ring-2 ring-primary/30 shadow-lg'
          : block.visible
            ? 'border-gray-200 dark:border-slate-700'
            : 'border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50'
      }`}
    >
      <GripVertical className="w-4 h-4 text-gray-400 dark:text-slate-500 cursor-grab" />

      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm ${block.visible ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-slate-500'}`}>
          {BLOCK_TYPE_LABELS[block.type]}
        </p>
        <p className={`text-xs truncate ${block.visible ? 'text-gray-500 dark:text-slate-400' : 'text-gray-400 dark:text-slate-500'}`}>
          {getBlockTitle(block)}
        </p>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onMoveUp(block.id)}
          disabled={isFirst}
          className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
          title="Nach oben"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onMoveDown(block.id)}
          disabled={isLast}
          className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
          title="Nach unten"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onToggleVisibility(block.id, !block.visible)}
          className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
          title={block.visible ? 'Ausblenden' : 'Einblenden'}
        >
          {block.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={() => onEdit(block)}
          className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/30"
          title="Bearbeiten"
        >
          <Pencil className="w-4 h-4" />
        </button>
        {!isProtected && (
          <button
            type="button"
            onClick={() => onDelete(block.id)}
            className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
            title="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}));
